import React, { useEffect, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { TickmillProductDto } from 'api/Clients/Types/TickmillProductDto'
import { NameDto } from 'api/Common/Types'
import { EditSubscription } from 'api/Trading-Accounts/Types/EditSubscription'
import { SubscriptionDetailsDto } from 'api/Trading-Accounts/Types/SubscriptionDetailsDto'
import { SubscriptionGroupDto } from 'api/Trading-Accounts/Types/SubscriptionGroupDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { AppState, useAppDispatch, useAppSelector } from 'store'
import {
  fetchClientMarketDataClassificationTypeAction,
  fetchTickmillProductsAction,
} from 'store/clients/clients.actions'
import {
  getCurrenciesAction,
  getTradingAccountSubscriptionBillingPeriodsAction,
  getTradingAccountSubscriptionGroupsAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'
import { isNumeric } from 'utils/is-numeric'

type SubscriptionsMainValues = EditSubscription

const SubscriptionsMainFormUI: React.FC<FormikProps<SubscriptionsMainValues> & OuterProps> = (
  props
) => {
  const {
    handleSubmit,
    values,
    errors,
    subscriptionGroups,
    currencies,
    billingPeriods,
    clientMarketDataClassificationTypes,
    tickmillProducts,
    resetForm,
  } = props

  const [isEdit, setEdit] = useState<boolean>(false)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleSubmit()
  }

  return (
    <Form onSubmit={handleSubmitForm}>
      <DataGrid
        columns={1}
        rightAddon={
          <ButtonGroup>
            {!isEdit ? (
              <Button type='button' onClick={() => setEdit(true)}>
                Edit
              </Button>
            ) : (
              <>
                <Button
                  type='button'
                  onClick={() => {
                    setEdit(false)
                    resetForm()
                  }}
                  variant='outline'
                >
                  Cancel
                </Button>
                <Button type='submit'>Save</Button>
              </>
            )}
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title='Subscription Details' columns={2}>
            <FormReadonlyField name='name' label='Name' value={values.name} />
            <FormReadonlyField name='description' label='Description' value={values.description} />
            <FormSelectField
              name='groupId'
              label='Group'
              options={subscriptionGroups.map((group) => ({
                id: group.id,
                name: group.name ?? '',
              }))}
              readOnly={!isEdit}
              error={errors.groupId}
              onChange={(value) => props.setFieldValue('groupId', value ?? '')}
            />
            <FormSelectField
              name='currencyId'
              label='Currency'
              options={currencies.map((currency) => ({ id: currency.id, name: currency.name }))}
              readOnly={!isEdit}
              error={errors.currencyId}
              onChange={(value) => props.setFieldValue('currencyId', value ?? '')}
            />
            <FormSelectField
              name='billingPeriodId'
              label='Billing Period'
              options={billingPeriods}
              readOnly={!isEdit}
              error={errors.billingPeriodId}
              onChange={(value) =>
                props.setFieldValue('billingPeriodId', value ? Number(value) : 0)
              }
            />
            <FormSelectField
              name='subscriptionClassificationTypeId'
              label='Classification'
              options={clientMarketDataClassificationTypes}
              readOnly={!isEdit}
              error={errors.subscriptionClassificationTypeId}
              onChange={(value) =>
                props.setFieldValue('subscriptionClassificationTypeId', value ? Number(value) : '')
              }
            />
            <FormCheckboxField
              name='enabled'
              label='Enabled'
              readOnly={!isEdit}
              error={errors.enabled}
            />
            <FormCheckboxField
              name='tradingAccountRequired'
              label='Trading Account Required'
              readOnly={!isEdit}
              error={errors.tradingAccountRequired}
            />
            <FormCheckboxField
              name='isThirdPartyTradingPlatform'
              label='Third Party Trading Platform'
              readOnly={!isEdit}
              error={errors.isThirdPartyTradingPlatform}
            />
            <FormSelectField
              name='tickmillProductId'
              label='Tickmill Product'
              options={tickmillProducts}
              readOnly={!isEdit}
              error={errors.tickmillProductId}
              onChange={(value) =>
                props.setFieldValue('tickmillProductId', value ? Number(value) : 0)
              }
            />
            <FormField
              name='order'
              label='Order'
              type='number'
              readOnly={!isEdit}
              error={errors.order}
            />
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

interface OuterProps {
  subscription: SubscriptionDetailsDto
  clientMarketDataClassificationTypes: NameDto[]
  billingPeriods: NameDto[]
  subscriptionGroups: SubscriptionGroupDto[]
  tickmillProducts: TickmillProductDto[]
  currencies: WalletCurrencyDto[]
  onSubmit(values: FormSubmitValues<SubscriptionsMainValues>): Promise<void>
}

const initialSubscriptionDetailState = (
  subscription: SubscriptionDetailsDto | undefined
): SubscriptionsMainValues => {
  return {
    id: subscription?.id || '',
    name: subscription?.name || '',
    description: subscription?.description || '',
    groupId: subscription?.group?.id || '',
    currencyId: subscription?.currency?.id || '',
    billingPeriodId: subscription?.billingPeriod?.id || 0,
    subscriptionClassificationTypeId: subscription?.subscriptionClassificationType?.id || '',
    enabled: subscription?.enabled || false,
    tradingAccountRequired: subscription?.tradingAccountRequired || false,
    documentId: subscription?.documentId || '',
    tickmillProductId: subscription?.tickmillProduct?.id || 0,
    order: subscription?.order || 0,
    isThirdPartyTradingPlatform: subscription?.isThirdPartyTradingPlatform || false,
  }
}

export const SubscriptionsDetailsMainForm = withFormik<OuterProps, SubscriptionsMainValues>({
  mapPropsToValues: ({ subscription }) => {
    return initialSubscriptionDetailState(subscription)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<SubscriptionsMainValues> = {}

    if (!values?.groupId) {
      errors.groupId = 'Required'
    }

    if (!values?.currencyId) {
      errors.currencyId = 'Required'
    }

    if (!values?.billingPeriodId) {
      errors.billingPeriodId = 'Required'
    }

    if (!values?.tickmillProductId) {
      errors.tickmillProductId = 'Required'
    }

    if (!isNumeric(values?.order)) {
      errors.order = 'Required'
    }

    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(SubscriptionsMainFormUI)

const useMapStateToProps = () => ({
  tickmillProducts: useAppSelector((state: AppState) => state.clients.tickmillProducts),
  subscriptionGroups: useAppSelector((state: AppState) => state.tradingAccounts.subscriptionGroups),
  clientMarketDataClassificationTypes: useAppSelector(
    (state: AppState) => state.clients.marketDataClassification
  ),
  billingPeriods: useAppSelector((state: AppState) => state.tradingAccounts.billingPeriods),
  currencies: useAppSelector((state: AppState) => state.tradingAccounts.currencies),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  return {
    getClientMarketDataClassificationTypes: async () =>
      dispatch(fetchClientMarketDataClassificationTypeAction(clientsApiClient)),
    getTradingAccountSubscriptionGroups: async () =>
      dispatch(getTradingAccountSubscriptionGroupsAction(tradingAccountsApiClient)),
    getTradingAccountSubscriptionBillingPeriods: async () =>
      dispatch(getTradingAccountSubscriptionBillingPeriodsAction(tradingAccountsApiClient)),
    getTickmillProducts: async () => dispatch(fetchTickmillProductsAction(clientsApiClient)),
    getCurrencies: async () => dispatch(getCurrenciesAction(tradingAccountsApiClient)),
  }
}

interface SubscriptionsMainProps {
  subscription: SubscriptionDetailsDto
  onRefresh(): void
}

export const SubscriptionsMainPage: React.FC<SubscriptionsMainProps> = (props) => {
  const { subscription, onRefresh } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const {
    currencies,
    subscriptionGroups,
    billingPeriods,
    clientMarketDataClassificationTypes,
    tickmillProducts,
  } = useMapStateToProps()
  const {
    getCurrencies,
    getTradingAccountSubscriptionGroups,
    getTradingAccountSubscriptionBillingPeriods,
    getClientMarketDataClassificationTypes,
    getTickmillProducts,
  } = useMapDispatchToProps()

  const handleSubmitForm = async (values: SubscriptionsMainValues) => {
    await tradingAccountsApiClient.updateTradingAccountSubscriptionById(subscription.id, values)
    onRefresh()
  }

  useEffect(() => {
    if (isEmpty(clientMarketDataClassificationTypes)) getClientMarketDataClassificationTypes()
    if (isEmpty(billingPeriods)) getTradingAccountSubscriptionBillingPeriods()
    if (isEmpty(subscriptionGroups)) getTradingAccountSubscriptionGroups()
    if (isEmpty(tickmillProducts)) getTickmillProducts()
    if (isEmpty(currencies)) getCurrencies()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SubscriptionsDetailsMainForm
      subscription={subscription}
      clientMarketDataClassificationTypes={clientMarketDataClassificationTypes}
      billingPeriods={billingPeriods}
      subscriptionGroups={subscriptionGroups}
      tickmillProducts={tickmillProducts}
      currencies={currencies}
      onSubmit={handleSubmitForm}
    />
  )
}
